import { Fragment, useEffect, useState } from 'react';

import KlaviyoForm from '@klv/atoms/klaviyo-form';
import HubspotForm from '@klv/atoms/hubspot-form';
import { Rive } from '@klv/atoms/rive';
import Buttons from '@klv/molecules/buttons';
import ImageLink from '@klv/molecules/image-link';
import MixedMedia from '@klv/molecules/mixed-media';
import VideoModalTrigger from '@klv/molecules/video-modal-trigger';
import Demo from '@klv/organisms/form/demo';
import { InHeroContainer } from '@klv/organisms/form/styles';
import Signup from '@klv/organisms/form/signup';
import { SingleLineForm } from '@klv/atoms/single-line-form';
import { useModal } from '@klv/context/modal';

import { convertNamedColorToColorVariant, generateClassName, getColorVariantFromBackground } from '@klv/shared/utils/functions';
import { getSessionStorageValues } from '@klaviyo-mkt/gatsby-components/organisms/form/utils';

import {
  ContentContainer, HorizontalHeroGrid,
  MediaContainer, StyledHero, TextHeroGrid,
  VerticalHeroGrid, HasEmbeddedForm,
} from './styles';
import * as styles from './styles/hero.module.scss';

import type { IFormState } from '@klv/organisms/form/types';
import type { TColorVariant } from '@klv/shared/styles/types';
import type { IHero, IHeroGrid, TEyebrowFont, THeroImageTypes } from './types';

const initialFormState: IFormState = {
  email: '',
  full_name: '',
  field_6: '',
  field_9: '',
  field_10: true,
  field_14: '',
  company_website: '',
  company_url: '',
  company: '',
  phone: '',
};

const HeroGrid = ({
  children, hasForm, hasImage, hasMedia, imageType, embeddedForm, textAlignment,
}: IHeroGrid) => {
  if (!hasMedia && embeddedForm === 'none') {
    return <TextHeroGrid textAlignment={textAlignment}>{children}</TextHeroGrid>;
  }

  if (imageType === 'horizontal' && embeddedForm === 'none') {
    return <HorizontalHeroGrid hasForm={hasForm} textAlignment={textAlignment}>{children}</HorizontalHeroGrid>;
  }

  return <VerticalHeroGrid embeddedForm={embeddedForm} hasForm={hasForm} $hasImage={hasImage}>{children}</VerticalHeroGrid>;
};

const Hero = ({
  background = 'lightSand',
  backgroundJpeg,
  demoFormHeader,
  demoFormRedirectUrl,
  embeddedForm = 'none',
  embeddedFormHeadline = '',
  embeddedFormBodyCopy = '',
  embeddedFormHeaderSubText = '',
  bodyCopy = '',
  buttons = [],
  eyebrow = '',
  eyebrowColor = 'defaultColor',
  eyebrowFont = 'defaultFont',
  formId = '',
  hubspotRedirectUrl = '',
  hubspotHeaderText = '',
  hubspotSubheaderText = '',
  headline = '',
  headlineFont,
  image,
  imageLink,
  imageType = 'horizontal',
  largeText,
  loopingVideo,
  mediaType = 'image',
  poster,
  pricingVariant = false,
  rive,
  showSingleLineForm,
  singleLineForm,
  videoHeadline,
  videoUrl,
  videoDuration,
  videoOverlayTitle,
  videoPlayButtonColor,
  demoFormSettings,
  textAlignment = 'left',
  ...rest
}: IHero) => {
  const modalStore = useModal();
  const { modalState } = modalStore;
  const backgroundColor = convertNamedColorToColorVariant(background as TColorVariant);
  const heroWithImage = !!image?.gatsbyImage;
  const hasForm = Boolean(formId);
  const hasMedia: boolean = heroWithImage || !!loopingVideo;
  const layout: THeroImageTypes = ['loopingVideo', 'videoModal'].includes(mediaType || '') ? 'vertical' : imageType;
  const [formState, setFormState] = useState(initialFormState);
  const isTwoColumn = embeddedForm === 'none' && (!hasMedia || imageType === 'vertical');
  const HeroContentsWrapper = embeddedForm === 'none' ? Fragment : HasEmbeddedForm;
  useEffect(() => {
    setFormState(getSessionStorageValues());
  }, []);
  
  // This let's us honor the line breaks from the CMS
  const formattedHeadline = headline ? headline.replace(/\n/g, '<br/>') : '';
  const HeroContents = (
    <>
      {eyebrow && <div className={generateClassName(['eyebrow', 'smPaddingTop', styles.eyebrow, styles[eyebrowColor], styles[eyebrowFont] as TEyebrowFont])}>{eyebrow}</div>}
      {headline && <h1 className={generateClassName([largeText ? 'display3' : 'h1', 'semiBold', styles.headline])} dangerouslySetInnerHTML={{ __html: formattedHeadline }} />}
      {bodyCopy && <div className={generateClassName([largeText ? styles.bigBodyCopy : '', styles.bodyCopy])} dangerouslySetInnerHTML={{ __html: bodyCopy }} />}
      {embeddedForm !== 'kform' && embeddedForm !== 'hubspotform' && embeddedForm !== 'demoHubSpot' && hasForm && <KlaviyoForm formId={formId} />}
      {showSingleLineForm && singleLineForm && 
      <div className={styles.singleLineFormContainer}>
        <SingleLineForm backgroundColor={background as TColorVariant} primaryButtonText={singleLineForm.primaryButtonText} placeholderText={singleLineForm.placeholderText} />
      </div>
      }
      <Buttons className={generateClassName([styles.riveButtons, textAlignment === 'center' ? styles.center : ''])} buttons={buttons} buttonColor={(background !== 'charcoal') ? 'dark' : 'light'} variants={['primary', 'ghost']} />
    </>
  );

  return (
    <StyledHero data-organism="hero" $isTwoColumn={isTwoColumn} $backgroundJpeg={backgroundJpeg} $background={background} $pricingVariant={pricingVariant} {...rest}>
      <HeroGrid embeddedForm={embeddedForm} hasForm={hasForm} hasImage={mediaType === 'image' && heroWithImage || mediaType === 'rive'} hasMedia={hasMedia} imageType={layout} textAlignment={textAlignment}>
        <HeroContentsWrapper>
        {rive?.riveFile ? (
          <div className={styles.contentContainer}>
            {HeroContents}
          </div>
        ) : (
          <ContentContainer $hasMedia={hasMedia}>
            {HeroContents}
          </ContentContainer>
        )}
          {mediaType === 'image' && heroWithImage && (
            <MediaContainer $imageType={layout}>
              <ImageLink backgroundColor={background as TColorVariant} image={image} imageLink={imageLink} loading='eager' />
            </MediaContainer>
          )}
          {mediaType === 'loopingVideo' && loopingVideo && poster?.gatsbyImage && (
            <MediaContainer $imageType="vertical">
              <MixedMedia mixedMedia={{ mediaType: 'video', video: { loop: true, videoUrl: loopingVideo, poster } }} />
            </MediaContainer>
          )}
          {mediaType === 'rive' && rive && (
            <div className={styles.rive}>
              <Rive riveFile={rive.riveFile} aspectRatio={rive?.aspectRatio} flexibleLayout={rive?.flexibleLayout} riveText={rive?.riveText} height={rive.height} />
            </div>
          )}
          {mediaType === 'videoModal' && heroWithImage && (
            <VideoModalTrigger
              videoDuration={videoDuration}
              modalHeadline={videoHeadline}
              videoUrl={videoUrl || ''}
              videoPlayButtonColor={videoPlayButtonColor}
              videoOverlayTitle={videoOverlayTitle}
            >
              <MediaContainer $imageType="vertical">
                <ImageLink backgroundColor={background as TColorVariant} image={image} imageLink={imageLink} />
              </MediaContainer>
            </VideoModalTrigger>
          )}
        </HeroContentsWrapper>
        {embeddedForm === 'kform' && !modalState.visibleModalId && hasForm &&
          <InHeroContainer>
            <KlaviyoForm formId={formId} />
          </InHeroContainer>
        }
        {embeddedForm === 'hubspotform' && !modalState.visibleModalId && hasForm &&
          <InHeroContainer>
            <HubspotForm redirectUrl={hubspotRedirectUrl} hubspotSubheaderText={hubspotSubheaderText} hubspotHeaderText={hubspotHeaderText} backgroundColor={getColorVariantFromBackground(background)} formId={formId} />
          </InHeroContainer>
        }
        {embeddedForm === 'signup' && !modalState.visibleModalId &&
          <Signup
            backgroundColor={backgroundColor}
            bodyCopy={embeddedFormBodyCopy}
            formState={formState}
            headline={embeddedFormHeadline}
            inHero={true}
            setFormState={setFormState}
            templateName="Default"
          />
        }
        {embeddedForm === 'signupInvisibleReCaptcha' && !modalState.visibleModalId &&
          <Signup
            backgroundColor={backgroundColor}
            bodyCopy={embeddedFormBodyCopy}
            formState={formState}
            headline={embeddedFormHeadline}
            inHero={true}
            setFormState={setFormState}
            templateName="Default"
            recaptchaInstance="invisible"
          />
        }
        
        {embeddedForm === 'signupTest' && !modalState.visibleModalId &&
          <Signup
            backgroundColor={backgroundColor}
            bodyCopy={embeddedFormBodyCopy}
            formState={formState}
            headline={embeddedFormHeadline}
            inHero={true}
            setFormState={setFormState}
            templateName="Default"
            buttonPlacementDecision={true}
          />
        }
        {['demo', 'demoHubSpot'].includes(embeddedForm) && !modalState.visibleModalId &&
          <Demo
            backgroundColor={backgroundColor}
            bodyCopy={embeddedFormHeaderSubText || embeddedFormBodyCopy}
            demoFormHeader={demoFormHeader}
            demoFormRedirectUrl={demoFormRedirectUrl}
            formState={formState}
            headline={embeddedFormHeadline}
            inHero={true}
            setFormState={setFormState}
            settings={demoFormSettings}
            templateName="Default"
            demoType={embeddedForm === 'demoHubSpot' ? 'demoHubSpot' : 'default'}
          />
        }
      </HeroGrid>
    </StyledHero>
  );
};

export default Hero;
